import React from 'react';
import { Helmet } from 'react-helmet';
import { ThemeProvider } from 'emotion-theming';
import { css, Global } from '@emotion/core';
import styled from '@emotion/styled';
import { useLocale, useTexts } from '@devsisters/gatsby-plugin-i18n';

import { asset } from '~/src/assets';
import { scaledCss } from '~/src/css';
import useHelloBraveCookiesSearchParams from '~/src/misc/useHelloBraveCookiesSearchParams';
import OpenGraph from '~/src/components/OpenGraph';
import KeyVisual from '~/src/components/KeyVisual';
import Title from '~/src/components/bravecookies/Title';
import Greeting from '~/src/components/bravecookies/sections/Greeting';
import HowTo from '~/src/components/bravecookies/sections/HowTo';
import ReceiveButton from '~/src/components/bravecookies/ReceiveButton';
import Footer from '~/src/components/bravecookies/Footer';
import { useScheduledRedirect } from '../misc/useScheduledRedirection';

const redirectDate = new Date('2020-07-30T09:00:00+09:00');

interface PageProps {
  location: Location;
}

const Page: React.FC<PageProps> = ({ location }) => {
  const { mid, nickname } = useHelloBraveCookiesSearchParams(location);
  const locale = useLocale();

  useScheduledRedirect(redirectDate, { toPath: `/${locale}/cookierunpuzzle/` });

  const t = useTexts();
  return (
    <ThemeProvider theme={{ breakpoints: ['640px'] }}>
      <Helmet>
        <html lang={locale} />
        <title>{t`$game.title`}</title>
        {process.env.FACEBOOK_APP_ID && (
          <meta property="fb:app_id" content={process.env.FACEBOOK_APP_ID} />
        )}
      </Helmet>
      <OpenGraph
        url={`${process.env.PUBLIC_URL}/${locale}/bravecookies/`}
        image={`${process.env.PUBLIC_URL}/bravecookies/opengraph/${locale}.jpg`}
        title={t`$game.title`}
        description={t`$title`}
      />
      <Global styles={globalStyles} />
      <Container>
        <KeyVisual backgroundImageSrc={asset('bravecookies/key-visual-background.png')}>
          <Title />
        </KeyVisual>
        <Greeting nickname={nickname} />
        <HowTo />
        {mid && <ReceiveButton mid={mid} />}
        <Footer />
      </Container>
    </ThemeProvider>
  );
};

export default Page;

const globalStyles = css`
  @import url('https://fonts.googleapis.com/css?family=Noto+Sans:300,400,700&font-display=swap');
  @import url('https://fonts.googleapis.com/css?family=Noto+Sans+KR&display=swap');
  @import url('https://fonts.googleapis.com/css?family=Noto+Sans+TC&display=swap');
  @import url('https://fonts.googleapis.com/css?family=Noto+Sans+JP&display=swap');
  @import url('https://fonts.devscookie.com/CookieRun/CookieRun.css');
  html,
  body {
    margin: 0;
    background-color: #000;
    line-height: 1;
    font-family: 'CookieRun', 'Noto Sans', sans-serif;
    &:lang(ja) {
      letter-spacing: -0.1em;
      font-family: 'Noto Sans JP', sans-serif;
      font-weight: bold;
    }
    &:lang(ko) {
      font-family: 'CookieRun', 'Noto Sans KR', sans-serif;
    }
    &:lang(zh-Hant) {
      font-family: 'Noto Sans TC', sans-serif;
    }
  }
  * {
    font-family: inherit;
    line-height: inherit;
  }
`;

const Container = styled('div')(
  scaledCss({
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    width: [640, '640px'],
    backgroundColor: '#dbf6ff',
  })
);
